
import {
  Vue, Component, Watch, Prop,
} from 'vue-property-decorator';
import moment from 'moment';
import { Route } from 'vue-router';
import TaskDetails from '@/components/TaskDetails.vue';
import LeadStatus from '@/components/LeadStatus.vue';
import LeadAssignDialog from '@/components/LeadAssignDialog.vue';
import LeadRenameChildDialog from '@/components/LeadRenameChildDialog.vue';
import LeadRemoveDialog from '@/components/LeadRemoveDialog.vue';
import LeadStatusAlert from '@/components/LeadStatusAlert.vue';
import EditTaskDialog from '@/components/EditTaskDialog.vue';
import {
  ChildTasks, Lead, PermissionCode, Task,
  LeadStatus as LeadStatusType,
} from '@/types';
import { currentUserHasPermission } from '@/helpers';
import { leadCode, leadRouteCode } from '@/helpers/leadCode';

moment.locale('it');

@Component({
  components: {
    TaskDetails,
    LeadStatus,
    LeadAssignDialog,
    LeadRenameChildDialog,
    LeadRemoveDialog,
    LeadStatusAlert,
    EditTaskDialog,
  },
})
export default class LeadWorkflowDetails extends Vue {
  @Prop(Boolean) readonly loading!: boolean

  editTaskDialog = false
  assignChildDialog = false
  renameChildDialog = false
  removeChildDialog = false

  assigning = false

  panels: number[] = []
  panelsMIR: number[] = []

  childPanels = {
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
    8: [],
    9: [],
    10: [],
    11: [],
    12: [],
  } as {
    [key: number]: number[];
  }

  selectedChildTasks: Task[] | null = null

  updatePanelsTimeout: ReturnType<typeof setTimeout> | null = null

  @Watch('$route')
  onRouteChange(val: Route) {
    console.log(`${this.constructor.name}: onRouteChange() val =`, val); // eslint-disable-line no-console

    if (val.name === 'Lead') {
      this.panels = [];
      Object.values(this.childPanels).forEach((panel, index) => {
        this.$set(this.childPanels, index, []);
      });

      this.$nextTick(() => {
        if (this.updatePanelsTimeout) clearTimeout(this.updatePanelsTimeout);

        this.updatePanelsTimeout = setTimeout(() => {
          this.updatePanels();

          // Se nella query è stato specificato un Task da modificare, lo apre
          // aggiunge l'id del padre nel caso sia il Task di una figlia
          if (val.query && 'editTask' in val.query && val.query.editTask) {
            this.$nextTick(() => {
              this.onEdit(`${val.query.editTask}`, this.lead.id);
            });
          }
        }, 1000);
      });
    }
  }

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console

    // Apre gli eventuali Task ancora da completare
    this.$nextTick(() => {
      if (this.updatePanelsTimeout) clearTimeout(this.updatePanelsTimeout);

      this.updatePanelsTimeout = setTimeout(() => {
        this.updatePanels();
      }, 1000);
    });
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get status(): LeadStatusType {
    return this.lead.status;
  }

  get open(): boolean {
    return this.lead && this.status === 'open';
  }

  get version() {
    return this.lead && 'version' in this.lead && this.lead.version ? Number(this.lead.version) : null;
  }

  get childLeads() {
    // return this.$store.getters.currentChildren;
    return this.$store.getters.currentChildrenInfo;
  }

  get user() {
    return this.$store.getters.currentUser;
  }

  get isSuperUser() {
    return this.user && this.user.isSuperUser;
  }

  get isRequester() {
    return this.user && this.lead && this.user.id === this.lead.requesterId;
  }

  get hasCategories() {
    return this.lead && typeof this.lead.flatCategories === 'string' && this.lead.flatCategories.length > 2;
  }

  get childTasks(): ChildTasks {
    if (!this.lead || this.version !== 3) return {};

    const childTasksObj: ChildTasks = {};

    this.lead.Tasks.forEach((lt: Task) => {
      if (lt.relationship === 'father' && !lt.name.endsWith('2')) {
        const count = lt.childCount ? lt.childCount : 1;
        if (count in childTasksObj) {
          childTasksObj[count].push(lt);
        } else {
          childTasksObj[count] = [lt];
        }
      }
    });

    return childTasksObj;
  }

  get hasChildren() {
    return Object.keys(this.childTasks).length > 0;
  }

  get isEcobonusCheckEnabled() {
    // Integrazione spenta: vedi EcobonusCheck.vue.
    if (!this.$store.getters.getIntegrations.ecobonusCheck.enabled) return false;
    if (
      !this.hasPermission('leads.ecobonuscheck')
      || !this.lead
      || ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.lead.type)
      || !this.hasCategories) return false;

    return this.lead.flatCategories && (
      this.lead.flatCategories.includes(':15:')
      || this.lead.flatCategories.includes(':21:')
      || this.lead.flatCategories.includes(':22:')
      || this.lead.flatCategories.includes(':23:')
    );
  }

  get isChild(): boolean {
    return ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.lead.type);
  }

  get isWorkflowVisible(): boolean {
    if (!this.hasPermission('leads.show_workflow')) return false;

    // Il super utente e il richiedente può sempre vedere il flusso di lavoro
    if (this.isSuperUser || this.isRequester) return true;

    return (this.isChild && this.lead.status !== 'new') || !this.isChild;
  }

  canShowChild(childTasks: Task[]) {
    const leadInfo = this.childLeadInfo(childTasks);

    if (!leadInfo) return false;

    const allowed = this.isSuperUser
      || leadInfo.allowedUsers.includes(`:${this.user.id}:`);

    return allowed;
  }

  canRemoveChild(childTasks: Task[]) {
    return 0 in childTasks && this.taskRemovable(childTasks[0]) && this.hasPermission(`leads_${this.lead.type}.task_child`);
  }

  canAssignChild(childTasks: Task[]) {
    return 0 in childTasks && this.taskAssignable(childTasks[0]) && this.hasPermission(`leads_${this.lead.type}.task_child`);
  }

  childClass(childTasks: Task[]) {
    const childLeadInfo = this.childLeadInfo(childTasks);

    if (!childLeadInfo) return 'unassigned';

    return childLeadInfo.status;
  }

  childLeadInfo(childTasks: Task[]) {
    // Ottiene la lead figlia da un elenco task assegnata
    if (0 in childTasks === false
      || 'childLeadId' in childTasks[0] === false
      || !childTasks[0].childLeadId) return null;

    return childTasks[0].childLeadId in this.childLeads
      ? this.childLeads[childTasks[0].childLeadId] : null;
  }

  childTitle(childTasks: Task[]) {
    if (0 in childTasks === false
      || 'childName' in childTasks[0] === false
      || !childTasks[0].childName) return 'Lead figlia';

    return childTasks[0].childName;
  }

  childType(childTasks: Task[]) {
    if (0 in childTasks === false
      || 'childType' in childTasks[0] === false
      || !childTasks[0].childType) return null;

    switch (childTasks[0].childType) {
      case 'installatore-figlia':
        return 'Lead figlia generica';

      case 'elettrica-figlia':
        return 'Lead figlia elettrica';

      case 'termica-figlia':
        return 'Lead figlia termica';

      default:
        break;
    }

    return null;
  }

  childId(childTasks: Task[]) {
    const childLeadInfo = this.childLeadInfo(childTasks);
    return childLeadInfo && 'id' in childLeadInfo ? childLeadInfo.id : null;
  }

  childCode(childTasks: Task[]) {
    const childLeadInfo = this.childLeadInfo(childTasks);
    // Composto dall'id perche' ProjectInfo porta solo id, status e allowedUsers
    return leadCode(childLeadInfo.id);
  }

  updatePanels() {
    // eslint-disable-next-line no-console
    console.log('LeadWorkflowDetails: updatePanels()');

    if (!this.open) return;

    if (this.version === 1) {
      if (this.lead.step1 === null) {
        this.panels.push(0);
      }
      if (this.lead.step2 === null) {
        this.panels.push(1);
      }
      if (this.lead.step3 === null) {
        this.panels.push(2);
      }
      if (this.lead.step4 === null) {
        this.panels.push(3);
      }
    } else if (this.version === 2) {
      if (this.lead.step2 === null) {
        this.panels.push(0);
      }
      if (this.lead.step3 === null) {
        this.panels.push(1);
      }
      if (this.lead.step4 === null) {
        this.panels.push(2);
      }
    } else if (this.version === 3) {
      if (!this.hasChildren) {
        // eslint-disable-next-line no-restricted-syntax
        for (const [index, task] of this.lead.Tasks.entries()) {
          if (!task.status) {
            this.panels.push(index + (this.isEcobonusCheckEnabled ? 1 : 0));
            break;
          }
        }
      } else {
        // eslint-disable-next-line no-restricted-syntax
        for (const [index, task] of this.lead.Tasks.filter((lt: Task) => lt.relationship !== 'father').entries()) {
          if (!task.status) {
            this.panels.push(index + (this.isEcobonusCheckEnabled ? 1 : 0));
            break;
          }
        }
      }
    }
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  // Verifica se il Task può essere assegnato
  taskAssignable(task: Task) {
    if (task.status === 'disabled'
      || this.lead.status !== 'open'
      || task.relationship !== 'father'
      || task.childLeadId !== null) {
      return false;
    }

    // const prevTask: Task | null = UTILS.getPrevTask(this.lead, task.name);

    // if (prevTask && (prevTask.status === null || prevTask.status === 'error')) {
    //   return false;
    // }

    return true;
  }

  // Verifica se il Task può essere rimosso
  taskRemovable(task: Task) {
    if (task.status === 'disabled'
      || this.lead.status !== 'open'
      || task.relationship !== 'father'
      || task.childLeadId !== null) {
      return false;
    }

    return true;
  }

  onShowChild(childTasks: Task[]) {
    console.log('LeadWorkflowDetails: onShowChild()'); // eslint-disable-line no-console

    const childLeadInfo = this.childLeadInfo(childTasks);

    console.log('LeadWorkflowDetails: onShowChild() - childLeadInfo =', childLeadInfo); // eslint-disable-line no-console

    if (childLeadInfo && 'id' in childLeadInfo && childLeadInfo.id) {
      this.$router.push({ name: 'Lead', params: { code: leadRouteCode(childLeadInfo.id) } });
      // this.$router.push({
      //   name: 'Lead',
      //   params: { code: leadRouteCode(childLeadInfo.id) },
      //   query: { tab: 'dashboard' },
      // });
    }
  }

  onAssignChild(childTasks: Task[]) {
    console.log('LeadWorkflowDetails: onAssignChild()'); // eslint-disable-line no-console

    this.selectedChildTasks = childTasks;

    this.assignChildDialog = true;
  }

  onAssignChildStart() {
    console.log('LeadWorkflowDetails: onAssignChildStart()'); // eslint-disable-line no-console

    // L'assegnazione è iniziata
    this.assigning = true;
  }

  onAssignChildDone() {
    console.log('LeadWorkflowDetails: onAssignChildDone()'); // eslint-disable-line no-console

    // L'assegnazione si è conclusa
    this.assigning = false;
  }

  onRenameChild(childTasks: Task[]) {
    this.selectedChildTasks = childTasks;

    this.renameChildDialog = true;
  }

  onRemoveChild(childTasks: Task[]) {
    this.selectedChildTasks = childTasks;

    this.removeChildDialog = true;
  }

  onEdit(name: string, leadId: number) {
    console.log(`${this.constructor.name}: onEdit() name = ${name} leadId = ${leadId}`); // eslint-disable-line no-console

    // Seleziona il Task (e la Lead a cui appartiene)
    // Se specificato un id del padre, recupera anche quello se necessario
    this.$store.dispatch('selectTask', {
      name,
      leadId,
      unselectLeadOnClose: false,
    });

    // Non attende e apre subito il dialog
    this.editTaskDialog = true;
  }

  onGoto(where: string) {
    console.log(`${this.constructor.name}: onGoto() where = ${where}`); // eslint-disable-line no-console

    this.$emit('goto', where);
  }

  onEditChild(name: string, leadId: number) {
    console.log(`${this.constructor.name}: onEditChild() name = ${name} leadId = ${leadId}`); // eslint-disable-line no-console

    if (leadId) {
      // Passa alla visualizzazione della figlia e richiede la modifica del Task
      const fatherId = (this.lead && this.lead.id !== leadId) ? this.lead.id : null;
      this.$router.push({
        name: 'Lead',
        params: { code: leadRouteCode(leadId), fatherId: `${fatherId}` },
        query: {
          tab: 'dashboard',
          editTask: name,
        },
      });
    }
  }
}
